(() => {
  if (window.__roadRexCoreLoaded) return;
  window.__roadRexCoreLoaded = true;

  const STORAGE_KEY = 'roadrex-garage-v1';
  const GARAGE_SCHEMA_VERSION = 2;
  const DEFAULT_FITMENT_FILTER = 'filter.p.m.custom.compatible_vehicles';
  const FIELDS = ['year', 'make', 'model', 'variant'];
  const AU_STATES = new Set(['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA']);

  const cleanValue = (value) =>
    value === null || value === undefined
      ? ''
      : String(value)
          .normalize('NFKC')
          .replace(/[\u2012-\u2015]/g, '-')
          .replace(/\b4\s*[x\u00d7]\s*4\b/gi, '4\u00d74')
          .replace(/\s+/g, ' ')
          .trim();

  const comparableValue = (value) =>
    cleanValue(value)
      .toLocaleLowerCase('en-AU')
      .replace(/\u00d7/g, 'x')
      .replace(/\s*([/&+-])\s*/g, '$1');

  const valuesEqual = (left, right) => comparableValue(left) === comparableValue(right);

  const boundedValue = (value, maxLength = 100) => cleanValue(value).slice(0, maxLength);

  const unique = (values) => {
    const seen = new Set();
    return values.filter((value) => {
      const key = comparableValue(value);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const readFirst = (record, keys) => {
    const key = keys.find((candidate) => record[candidate] !== undefined && record[candidate] !== null && record[candidate] !== '');
    return key ? record[key] : '';
  };

  const yearNumber = (value) => {
    const parsed = Number.parseInt(cleanValue(value), 10);
    return Number.isInteger(parsed) && parsed >= 1900 && parsed <= 2100 ? parsed : null;
  };

  const parseYearRange = (record) => {
    const rawYear = cleanValue(readFirst(record, ['year', 'modelYear', 'model_year']));
    const rangeMatch = rawYear.match(/(19\d{2}|20\d{2}|2100)\D+(19\d{2}|20\d{2}|2100)/);
    let from = yearNumber(readFirst(record, ['yearFrom', 'year_from', 'startYear', 'start_year', 'yearStart', 'year_start']));
    let to = yearNumber(readFirst(record, ['yearTo', 'year_to', 'endYear', 'end_year', 'yearEnd', 'year_end']));

    if (!from && rangeMatch) from = yearNumber(rangeMatch[1]);
    if (!to && rangeMatch) to = yearNumber(rangeMatch[2]);
    if (!from) from = yearNumber(rawYear);
    if (!to) to = from;
    if (!from || !to) return { yearFrom: null, yearTo: null };
    if (from > to) [from, to] = [to, from];
    return { yearFrom: from, yearTo: to };
  };

  const normalizeRecord = (record) => {
    if (!record || typeof record !== 'object' || Array.isArray(record)) return null;

    const { yearFrom, yearTo } = parseYearRange(record);
    const fitmentId = cleanValue(readFirst(record, ['fitmentId', 'fitment_id', 'metaobjectId', 'metaobject_id', 'id', 'gid']));
    const handle = cleanValue(readFirst(record, ['handle', 'vehicleHandle', 'vehicle_handle']));
    const make = cleanValue(record.make);
    const model = cleanValue(readFirst(record, ['model', 'modelName', 'model_name']));
    const variant = cleanValue(readFirst(record, ['variant', 'series', 'body', 'bodyStyle', 'body_style']));

    if (!yearFrom || !yearTo || !make || !model || !variant) return null;

    return {
      yearFrom,
      yearTo,
      make,
      model,
      variant,
      modelAliases: unique([
        model,
        readFirst(record, ['modelName', 'model_name']),
        readFirst(record, ['modelDisplay', 'model_display']),
        readFirst(record, ['series']),
        [readFirst(record, ['modelName', 'model_name']), readFirst(record, ['series'])].map(cleanValue).filter(Boolean).join(' ')
      ].map(cleanValue)),
      variantAliases: unique([
        variant,
        readFirst(record, ['variantName', 'variant_name']),
        readFirst(record, ['variantDisplay', 'variant_display']),
        readFirst(record, ['body', 'bodyStyle', 'body_style'])
      ].map(cleanValue)),
      fitmentId,
      handle,
      key: fitmentId || handle || [yearFrom, yearTo, make, model, variant].map(comparableValue).join('|'),
      url: cleanValue(readFirst(record, ['url', 'resultsUrl', 'results_url', 'collectionUrl', 'collection_url'])),
      filterUrl: cleanValue(readFirst(record, ['filterUrl', 'filter_url'])),
      filterParameter: cleanValue(readFirst(record, ['filterParameter', 'filter_parameter'])),
      filterValue: cleanValue(readFirst(record, ['filterValue', 'filter_value'])) || fitmentId
    };
  };

  const recordYears = (record) => {
    const years = [];
    for (let year = record.yearTo; year >= record.yearFrom; year -= 1) years.push(String(year));
    return years;
  };

  const recordSupportsYear = (record, year) => {
    const selectedYear = yearNumber(year);
    return Boolean(selectedYear && selectedYear >= record.yearFrom && selectedYear <= record.yearTo);
  };

  const recordMatchesVehicle = (record, vehicle) =>
    recordSupportsYear(record, vehicle.year) &&
    ['make', 'model', 'variant'].every((field) => valuesEqual(record[field], vehicle[field]));

  const recordRangeSpan = (record) => Math.max(0, Number(record.yearTo) - Number(record.yearFrom));

  const uniqueRecords = (records) => {
    const seen = new Set();
    return records.filter((record) => {
      const key = cleanValue(record.fitmentId || record.key || record.handle);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const mostSpecificRecords = (records) => {
    const available = uniqueRecords(records);
    if (available.length < 2) return available;
    const smallestSpan = Math.min(...available.map(recordRangeSpan));
    return available.filter((record) => recordRangeSpan(record) === smallestSpan);
  };

  const comparablePath = (url) => {
    try {
      return decodeURIComponent(url.pathname).replace(/\/+$/, '') || '/';
    } catch (_error) {
      return url.pathname.replace(/\/+$/, '') || '/';
    }
  };

  const readStoredVehicle = (key) => {
    try {
      const vehicle = JSON.parse(window.localStorage.getItem(key) || 'null');
      return vehicle && typeof vehicle === 'object' && !Array.isArray(vehicle) ? vehicle : null;
    } catch (_error) {
      return null;
    }
  };

  const saveStoredVehicle = (key, vehicle) => {
    try {
      window.localStorage.setItem(key, JSON.stringify(vehicle));
    } catch (_error) {
      // Browsing and fitment selection still work if storage is unavailable.
    }
  };

  const clearStoredVehicle = (key) => {
    try {
      window.localStorage.removeItem(key);
    } catch (_error) {
      // Ignore storage restrictions.
    }
  };

  const vehicleLabel = (vehicle) => {
    if (!vehicle || typeof vehicle !== 'object') return '';
    const variant = cleanValue(vehicle.variant);
    const genericVariant = /^(all|any)\b.*\b(variant|model|trim|fitment)/i.test(variant);
    const structuredLabel = [vehicle.year, vehicle.make, vehicle.model, genericVariant ? '' : variant]
      .map(cleanValue)
      .filter(Boolean)
      .join(' ');
    return structuredLabel || cleanValue(vehicle.label);
  };

  const vehicleSearchLabel = (vehicle) => {
    if (!vehicle || typeof vehicle !== 'object') return '';
    const variant = cleanValue(vehicle.variant);
    const genericVariant = /^(all|any)\b.*\b(variant|model|trim|fitment)/i.test(variant);
    return [vehicle.year, vehicle.make, vehicle.model, genericVariant ? '' : variant].map(cleanValue).filter(Boolean).join(' ');
  };

  const syncVehicleLabels = (vehicle) => {
    document.querySelectorAll('[data-rr-current-vehicle]').forEach((node) => {
      node.textContent = vehicleSearchLabel(vehicle) || vehicleLabel(vehicle) || node.dataset.rrEmptyLabel || node.dataset.emptyLabel || 'Choose your 4\u00d74';
    });
  };

  class RoadRexVehicleFinder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.form = this.querySelector('[data-rr-manual-form]');
      this.status = this.querySelector('[data-rr-finder-status]');
      this.mobileTrigger = this.querySelector('[data-rr-finder-mobile-trigger]');
      this.lookupTabs = this.querySelector('[data-rr-lookup-tabs]');
      this.modeButtons = [...this.querySelectorAll('[data-rr-finder-mode]')];
      this.regoForm = this.querySelector('[data-rr-rego-form]');
      this.regoInput = this.regoForm?.querySelector('[name="registration"]') || null;
      this.regoState = this.regoForm?.querySelector('[name="registration_state"]') || null;
      this.regoEndpoint = this.resolveRegoEndpoint(this.dataset.regoEndpoint);
      this.selects = Object.fromEntries(FIELDS.map((field) => [field, this.querySelector(`[name="${field}"]`)]));

      try {
        const parsedRecords = JSON.parse(this.querySelector('[data-rr-vehicle-data]')?.textContent || '[]');
        this.records = Array.isArray(parsedRecords) ? parsedRecords.map(normalizeRecord).filter(Boolean) : [];
      } catch (_error) {
        this.records = [];
      }

      this.storageKey = this.dataset.storageKey || STORAGE_KEY;
      this.autoSelectSingle = this.dataset.autoSelectSingle === 'true';
      this.preselectDefaults =
        this.dataset.preselectDefaults === 'true' ||
        this.dataset.preselectDefault === 'true' ||
        this.dataset.autoSelectDefaults === 'true';
      this.placeholders = Object.fromEntries(
        FIELDS.map((field) => [
          field,
          this.selects[field]?.querySelector('option[value=""]')?.textContent?.trim() || `Select ${this.fieldLabel(field)}`
        ])
      );

      FIELDS.forEach((field, index) => {
        this.selects[field]?.addEventListener('change', () => this.handleFieldChange(index));
      });

      this.mobileTrigger?.addEventListener('click', () => {
        this.classList.add('is-expanded');
        this.mobileTrigger.setAttribute('aria-expanded', 'true');
        window.setTimeout(() => this.selects.year?.focus(), 50);
      });

      this.form?.addEventListener('submit', (event) => this.handleSubmit(event));
      this.regoForm?.addEventListener('submit', (event) => this.handleRegoSubmit(event));
      this.modeButtons.forEach((button, index) => {
        button.addEventListener('click', () => this.setLookupMode(button.dataset.rrFinderMode, true));
        button.addEventListener('keydown', (event) => this.handleModeKeydown(event, index));
      });

      if (this.dataset.regoEnabled === 'true' && this.regoForm && this.regoEndpoint) {
        const defaultMode = this.dataset.regoDefaultMode === 'rego' ? 'rego' : 'manual';
        this.setLookupMode(defaultMode);
      } else {
        this.disableRegoLookup();
      }

      this.hydrate();
    }

    message(key, fallback, replacements = {}) {
      let output = this.dataset[key] || fallback;
      Object.entries(replacements).forEach(([token, value]) => {
        output = output.split(`[${token}]`).join(String(value || ''));
      });
      return output;
    }

    fieldLabel(field) {
      return this.dataset[`${field}Label`] || field;
    }

    resolveRegoEndpoint(value) {
      const raw = boundedValue(value, 2048);
      if (!raw || raw.startsWith('//')) return null;

      try {
        const endpoint = new URL(raw, window.location.origin);
        const isSameStoreProxy = raw.startsWith('/') && !raw.startsWith('//');
        if (endpoint.protocol !== 'https:' || endpoint.username || endpoint.password) return null;
        if (isSameStoreProxy && endpoint.origin !== window.location.origin) return null;
        return endpoint;
      } catch (_error) {
        return null;
      }
    }

    disableRegoLookup() {
      if (this.lookupTabs) this.lookupTabs.hidden = true;
      if (this.regoForm) this.regoForm.hidden = true;
      if (this.form) this.form.hidden = false;
      this.dataset.lookupMode = 'manual';
    }

    setLookupMode(mode, moveFocus = false) {
      const nextMode = mode === 'rego' && this.regoEndpoint ? 'rego' : 'manual';
      this.dataset.lookupMode = nextMode;

      this.modeButtons.forEach((button) => {
        const active = button.dataset.rrFinderMode === nextMode;
        button.setAttribute('aria-selected', String(active));
        button.tabIndex = active ? 0 : -1;
        if (active && moveFocus) button.focus();
      });

      if (this.regoForm) this.regoForm.hidden = nextMode !== 'rego';
      if (this.form) this.form.hidden = nextMode !== 'manual';
      if (this.status) this.status.textContent = '';
      this.dataset.state = '';

      if (moveFocus) {
        window.setTimeout(() => {
          if (nextMode === 'rego') this.regoInput?.focus();
          else this.selects.year?.focus();
        }, 50);
      }
    }

    handleModeKeydown(event, index) {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key) || this.modeButtons.length < 2) return;
      event.preventDefault();
      let nextIndex = index;
      if (event.key === 'ArrowRight') nextIndex = (index + 1) % this.modeButtons.length;
      if (event.key === 'ArrowLeft') nextIndex = (index - 1 + this.modeButtons.length) % this.modeButtons.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = this.modeButtons.length - 1;
      const nextButton = this.modeButtons[nextIndex];
      this.setLookupMode(nextButton?.dataset.rrFinderMode || 'manual', true);
    }

    readProviderVehicle(payload, registration, state) {
      if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null;

      const candidates = [
        payload.vehicle,
        payload.data?.vehicle,
        payload.result?.vehicle,
        payload.data,
        payload.result,
        payload
      ].filter((candidate) => candidate && typeof candidate === 'object' && !Array.isArray(candidate));
      const read = (keys, maxLength = 100) => {
        for (const candidate of candidates) {
          const value = readFirst(candidate, keys);
          if (value !== '') return boundedValue(value, maxLength);
        }
        return '';
      };

      const year = yearNumber(read(['year', 'Year', 'modelYear', 'model_year', 'manufactureYear', 'manufacture_year'], 12));
      const make = read(['make', 'Make', 'manufacturer', 'vehicleMake', 'vehicle_make']);
      const model = read(['model', 'Model', 'vehicleModel', 'vehicle_model']);
      if (!year || !make || !model) return null;

      const series = read(['series', 'Series', 'badge', 'Badge', 'submodel', 'subModel', 'sub_model']);
      const variant = read(['variant', 'Variant', 'grade', 'Grade', 'trim', 'Trim']);
      const body = read(['body', 'Body', 'bodyType', 'body_type', 'bodyStyle', 'body_style']);
      const engine = read(['engine', 'Engine', 'engineDescription', 'engine_description']);

      return {
        year: String(year),
        make,
        model,
        series,
        variant,
        body,
        engine,
        registration: boundedValue(registration, 10),
        state: boundedValue(state, 3)
      };
    }

    providerVehicleLabel(vehicle) {
      const descriptor = unique([vehicle.series, vehicle.variant, vehicle.body].map(cleanValue)).join(' ');
      const vehicleName = [vehicle.year, vehicle.make, vehicle.model, descriptor].map(cleanValue).filter(Boolean).join(' ');
      const rego = vehicle.registration && vehicle.state ? `Rego ${vehicle.state} ${vehicle.registration}` : '';
      return [vehicleName, rego].filter(Boolean).join(' · ');
    }

    matchProviderVehicle(vehicle) {
      const providerModelAliases = unique([
        vehicle.model,
        vehicle.series,
        [vehicle.model, vehicle.series].map(cleanValue).filter(Boolean).join(' ')
      ].map(cleanValue));
      const providerVariantAliases = unique([vehicle.variant, vehicle.body].map(cleanValue));

      const scored = this.records
        .filter((record) => recordSupportsYear(record, vehicle.year) && valuesEqual(record.make, vehicle.make))
        .map((record) => {
          const modelExact = providerModelAliases.some((providerValue) =>
            (record.modelAliases || [record.model]).some((recordValue) => valuesEqual(providerValue, recordValue))
          );
          if (!modelExact) return { record, score: -1 };

          const variantExact = providerVariantAliases.some((providerValue) =>
            (record.variantAliases || [record.variant]).some((recordValue) => valuesEqual(providerValue, recordValue))
          );
          return { record, score: 6 + (variantExact ? 4 : 0) };
        })
        .filter((candidate) => candidate.score >= 6)
        .sort((left, right) => right.score - left.score);

      if (!scored.length) return null;
      const bestScore = scored[0].score;
      const best = scored.filter((candidate) => candidate.score === bestScore);
      if (best.length === 1) return best[0].record;

      const logicalVehicles = unique(
        best.map(({ record }) => [record.make, record.model, record.variant].map(comparableValue).join('|'))
      );
      if (logicalVehicles.length !== 1) return null;

      return best.map(({ record }) => record).sort((left, right) => recordRangeSpan(left) - recordRangeSpan(right))[0];
    }

    regoFilterDestination(record, vehicle) {
      const matchingRecords = vehicle ? this.recordsMatchingVehicle(vehicle) : [record];
      if (!matchingRecords.some((candidate) => candidate?.filterValue)) return null;
      let destination;
      try {
        destination = new URL(this.dataset.resultsUrl || '/collections/all', window.location.origin);
      } catch (_error) {
        destination = new URL('/collections/all', window.location.origin);
      }
      if (destination.origin !== window.location.origin) destination = new URL('/collections/all', window.location.origin);
      const filterParam = cleanValue(record?.filterParameter || this.dataset.fitmentFilterParameter) || DEFAULT_FITMENT_FILTER;
      const filterValues = unique(matchingRecords.map((candidate) => candidate.filterValue).filter(Boolean));
      destination.searchParams.set(filterParam, filterValues.join(','));
      return destination;
    }

    openRegoFitmentEnquiry(vehicle, opener) {
      const enquiryVehicle = {
        schemaVersion: GARAGE_SCHEMA_VERSION,
        year: vehicle?.year || '',
        make: vehicle?.make || '',
        model: vehicle?.model || '',
        variant: unique([vehicle?.series, vehicle?.variant, vehicle?.body].map(cleanValue)).join(' '),
        registration: vehicle?.registration || boundedValue(this.regoInput?.value, 10),
        state: vehicle?.state || boundedValue(this.regoState?.value, 3),
        source: 'registration_lookup',
        savedAt: new Date().toISOString()
      };
      enquiryVehicle.label = vehicle ? this.providerVehicleLabel(vehicle) : `Rego ${enquiryVehicle.state} ${enquiryVehicle.registration}`.trim();
      document.dispatchEvent(new CustomEvent('rr:fitment-unavailable', { detail: { vehicle: enquiryVehicle, opener } }));
    }

    async handleRegoSubmit(event) {
      event.preventDefault();
      if (this.regoSubmitting || !this.regoEndpoint) return;

      const registration = boundedValue(this.regoInput?.value, 10).toUpperCase().replace(/[\s-]/g, '');
      const state = boundedValue(this.regoState?.value, 3).toUpperCase();
      const submitButton = this.regoForm?.querySelector('.rr-finder__rego-submit');

      if (!registration || !state) {
        this.dataset.state = 'error';
        if (this.status) this.status.textContent = this.message('regoMissingMessage', 'Enter a registration and select its state.');
        (!registration ? this.regoInput : this.regoState)?.focus();
        return;
      }

      if (!/^[A-Z0-9]{1,10}$/.test(registration) || !AU_STATES.has(state)) {
        this.dataset.state = 'error';
        if (this.status) this.status.textContent = this.message('regoInvalidMessage', 'Enter a valid Australian registration using letters and numbers only.');
        this.regoInput?.focus();
        return;
      }

      this.regoSubmitting = true;
      this.dataset.state = 'loading';
      submitButton?.setAttribute('aria-busy', 'true');
      if (submitButton) submitButton.disabled = true;
      if (this.status) this.status.textContent = this.message('regoCheckingMessage', 'Checking the Australian vehicle register…');
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 12000);

      try {
        const response = await fetch(this.regoEndpoint.toString(), {
          method: 'POST',
          credentials: this.regoEndpoint.origin === window.location.origin ? 'same-origin' : 'omit',
          mode: this.regoEndpoint.origin === window.location.origin ? 'same-origin' : 'cors',
          redirect: 'error',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ registration, state, locale: 'en-AU' }),
          signal: controller.signal
        });
        if (!response.ok) throw new Error('Registration lookup failed');

        const contentLength = Number.parseInt(response.headers.get('content-length') || '0', 10);
        if (contentLength > 100000) throw new Error('Registration lookup response is too large');
        const responseText = await response.text();
        if (responseText.length > 100000) throw new Error('Registration lookup response is too large');

        let payload;
        try {
          payload = JSON.parse(responseText);
        } catch (_error) {
          throw new Error('Registration lookup returned invalid JSON');
        }

        const providerVehicle = this.readProviderVehicle(payload, registration, state);
        const record = providerVehicle ? this.matchProviderVehicle(providerVehicle) : null;
        const destination = record ? this.regoFilterDestination(record, providerVehicle) : null;

        if (!providerVehicle || !record || !destination) {
          const fallbackVehicle = providerVehicle || { registration, state };
          const fallbackLabel = providerVehicle ? this.providerVehicleLabel(providerVehicle) : `Rego ${state} ${registration}`;
          this.dataset.state = 'error';
          if (this.status) {
            this.status.textContent = this.message('regoNoMatchMessage', 'We found [vehicle], but it is not mapped in the Road Rex 4x4 catalogue yet. Opening a manual fitment check.', { vehicle: fallbackLabel });
          }
          if (this.dataset.designMode !== 'true') this.openRegoFitmentEnquiry(fallbackVehicle, submitButton);
          return;
        }

        const matchingRecords = this.recordsMatchingVehicle({
          year: providerVehicle.year,
          make: record.make,
          model: record.model,
          variant: record.variant
        });
        const storedVehicle = this.vehicleFromRecord(record, providerVehicle.year, destination.toString(), matchingRecords);
        storedVehicle.registration = registration;
        storedVehicle.state = state;
        storedVehicle.source = 'registration_lookup';
        storedVehicle.url = destination.toString();
        saveStoredVehicle(this.storageKey, storedVehicle);
        syncVehicleLabels(storedVehicle);
        document.dispatchEvent(new CustomEvent('rr:vehicle-change', { detail: storedVehicle }));
        this.dataset.state = 'saved';
        if (this.status) {
          this.status.textContent = this.message('regoSavedMessage', '[vehicle] saved to your garage.', { vehicle: vehicleLabel(storedVehicle) });
        }
        if (this.dataset.designMode !== 'true') window.setTimeout(() => window.location.assign(storedVehicle.url), 220);
      } catch (_error) {
        this.dataset.state = 'error';
        if (this.status) {
          this.status.textContent = this.message('regoErrorMessage', 'We could not complete that registration lookup. Choose your vehicle manually or ask Road Rex 4x4 for help.');
        }
      } finally {
        window.clearTimeout(timeout);
        this.regoSubmitting = false;
        submitButton?.removeAttribute('aria-busy');
        if (submitButton) submitButton.disabled = false;
      }
    }

    hydrate() {
      const defaults = Object.fromEntries(FIELDS.map((field) => [field, this.dataset[`default${field[0].toUpperCase()}${field.slice(1)}`] || '']));
      const stored = readStoredVehicle(this.storageKey);
      const refreshedStored = stored ? this.refreshStoredVehicle(stored) : null;
      const defaultRecord = this.preselectDefaults ? this.findRecord(defaults) : null;
      const preferred = refreshedStored || (defaultRecord ? this.vehicleFromRecord(defaultRecord, defaults.year) : null);

      if (stored && !refreshedStored) clearStoredVehicle(this.storageKey);

      FIELDS.forEach((field, index) => {
        this.populateField(field, index, preferred?.[field] || '');
      });

      if (refreshedStored) {
        saveStoredVehicle(this.storageKey, refreshedStored);
        syncVehicleLabels(refreshedStored);
      } else if (preferred && this.getSelectedRecord()) syncVehicleLabels(preferred);

      this.updateReadyState();
    }

    recordExists(vehicle) {
      return Boolean(this.findRecord(vehicle));
    }

    findRecord(vehicle) {
      if (!vehicle || typeof vehicle !== 'object') return null;

      const fitmentId = cleanValue(vehicle.fitmentId || vehicle.fitment_id);
      const fitmentKey = cleanValue(vehicle.fitmentKey || vehicle.key);
      let record = fitmentId ? this.records.find((candidate) => candidate.fitmentId && candidate.fitmentId === fitmentId) : null;
      if (!record && fitmentKey) record = this.records.find((candidate) => candidate.key === fitmentKey || candidate.handle === fitmentKey);
      if (record && recordSupportsYear(record, vehicle.year)) return record;
      return this.records.find((candidate) => recordMatchesVehicle(candidate, vehicle)) || null;
    }

    recordsMatchingVehicle(vehicle) {
      if (!vehicle || typeof vehicle !== 'object') return [];
      return mostSpecificRecords(this.records.filter((candidate) => recordMatchesVehicle(candidate, vehicle)));
    }

    refreshStoredVehicle(vehicle) {
      const record = this.findRecord(vehicle);
      if (!record || !recordSupportsYear(record, vehicle.year)) return null;
      const matchingRecords = this.recordsMatchingVehicle({
        year: vehicle.year,
        make: record.make,
        model: record.model,
        variant: record.variant
      });
      const refreshed = this.vehicleFromRecord(record, vehicle.year, '', matchingRecords);
      const destination = this.nativeFilterDestination(matchingRecords) || (record.url ? new URL(record.url, window.location.origin) : null);
      refreshed.url = destination ? destination.toString() : '';
      return refreshed;
    }

    vehicleFromRecord(record, year, url = '', matchingRecords = [record]) {
      const records = uniqueRecords(matchingRecords.length ? matchingRecords : [record]);
      const vehicle = {
        schemaVersion: GARAGE_SCHEMA_VERSION,
        year: String(yearNumber(year) || record.yearFrom),
        make: record.make,
        model: record.model,
        variant: record.variant,
        fitmentId: record.fitmentId,
        fitmentKey: record.key,
        handle: record.handle,
        fitmentIds: unique(records.map((candidate) => candidate.fitmentId).filter(Boolean)),
        fitmentKeys: unique(records.map((candidate) => candidate.key).filter(Boolean)),
        handles: unique(records.map((candidate) => candidate.handle).filter(Boolean)),
        url: cleanValue(url || record.url),
        savedAt: new Date().toISOString()
      };
      vehicle.label = vehicleLabel(vehicle);
      return vehicle;
    }

    candidatesBefore(index) {
      const previousFields = FIELDS.slice(0, index);
      if (previousFields.some((field) => !this.selects[field]?.value)) return [];

      return this.records.filter((record) =>
        previousFields.every((field) => {
          const selectedValue = this.selects[field]?.value || '';
          return field === 'year' ? recordSupportsYear(record, selectedValue) : valuesEqual(record[field], selectedValue);
        })
      );
    }

    populateField(field, index, preferredValue = '') {
      const select = this.selects[field];
      if (!select) return;

      const candidates = index === 0 ? this.records : this.candidatesBefore(index);
      const values = unique(
        field === 'year' ? candidates.flatMap(recordYears) : candidates.map((record) => record[field])
      );
      if (field === 'year') values.sort((a, b) => Number(b) - Number(a));
      else values.sort((a, b) => a.localeCompare(b));

      const previous = cleanValue(preferredValue || select.value);
      select.replaceChildren(new Option(this.placeholders[field], ''));
      values.forEach((value) => select.add(new Option(value, value)));
      select.disabled =
        values.length === 0 ||
        (index > 0 && FIELDS.slice(0, index).some((previousField) => !this.selects[previousField]?.value));
      if (!select.disabled && previous) {
        const matchingValue = values.find((value) => valuesEqual(value, previous));
        select.value = matchingValue || '';
      } else select.value = '';

      if (!select.disabled && !select.value && values.length === 1 && this.autoSelectSingle) select.value = values[0];
    }

    handleFieldChange(index) {
      FIELDS.slice(index + 1).forEach((field, offset) => {
        if (this.selects[field]) this.selects[field].value = '';
        this.populateField(field, index + offset + 1);
      });
      this.dataset.state = '';
      if (this.status) this.status.textContent = '';
      this.updateReadyState();
    }

    updateReadyState() {
      const ready = FIELDS.every((field) => Boolean(this.selects[field]?.value)) && Boolean(this.getSelectedRecord());
      this.dataset.ready = String(ready);
      this.form?.toggleAttribute('data-rr-ready', ready);
    }

    currentSelection() {
      return Object.fromEntries(FIELDS.map((field) => [field, this.selects[field]?.value || '']));
    }

    getSelectedRecord() {
      return this.getSelectedRecords()[0] || null;
    }

    getSelectedRecords() {
      return this.recordsMatchingVehicle(this.currentSelection());
    }

    nativeFilterDestination(records) {
      const matchingRecords = uniqueRecords(Array.isArray(records) ? records : [records].filter(Boolean));
      const filterValues = unique(matchingRecords.map((record) => record.filterValue).filter(Boolean));
      if (this.dataset.useNativeFilter !== 'true' || !filterValues.length) return null;
      const primaryRecord = matchingRecords[0];
      const baseUrl = primaryRecord.filterUrl || primaryRecord.url || this.dataset.resultsUrl || '/collections/all';
      const destination = new URL(baseUrl, window.location.origin);
      const filterParam =
        primaryRecord.filterParameter ||
        cleanValue(this.dataset.fitmentFilterParameter || this.dataset.fitmentFilterParam || this.dataset.filterParam) ||
        DEFAULT_FITMENT_FILTER;
      // Shopify applies OR semantics to comma-separated values from the same filter.
      // This lets one selected year match every exact application range containing it.
      destination.searchParams.set(filterParam, filterValues.join(','));
      return destination;
    }

    tagSearchDestination(records, selection) {
      const handles = unique(records.map((record) => record.handle).filter(Boolean));
      if (!handles.length) return null;

      const destination = new URL(this.dataset.tagSearchUrl || '/search', window.location.origin);
      const query = handles.map((handle) => `tag:\"${handle.replace(/[\\\"]/g, '\\$&')}\"`).join(' OR ');
      destination.searchParams.set('q', handles.length > 1 ? `(${query})` : query);
      destination.searchParams.set('type', 'product');
      destination.searchParams.set('options[prefix]', 'none');
      destination.searchParams.set('rr_vehicle_label', vehicleSearchLabel(selection) || vehicleLabel(selection));
      return destination;
    }

    async verifyTaggedCollection(destination) {
      const requestPath = comparablePath(destination);
      const response = await fetch(destination.toString(), {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
        redirect: 'follow',
        method: 'GET'
      });

      if (!response.ok) return false;
      if (comparablePath(new URL(response.url, window.location.origin)) !== requestPath) return false;

      const responseDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
      const resultCards = responseDocument.querySelectorAll('[id^="RRCatalogGrid-"] [data-product-card], .rr-catalog__products [data-product-card], #product-grid [data-product-card], .rr-search__grid [data-product-card]');
      return resultCards.length > 0;
    }

    async handleSubmit(event) {
      event.preventDefault();
      if (this.submitting) return;

      const selection = this.currentSelection();
      const missingField = FIELDS.find((field) => !selection[field]);

      if (missingField) {
        this.classList.add('is-expanded');
        this.dataset.state = 'error';
        if (this.status) this.status.textContent = this.message('missingFieldMessage', 'Please select a [field].', { field: this.fieldLabel(missingField).toLowerCase() });
        this.selects[missingField]?.focus();
        return;
      }

      const matchingRecords = this.getSelectedRecords();
      const record = matchingRecords[0];
      if (!record || !matchingRecords.length) {
        this.dataset.state = 'error';
        if (this.status) this.status.textContent = this.message('invalidCombinationMessage', 'That vehicle combination is not available yet. Ask Road Rex 4x4 for a manual fitment check.');
        return;
      }

      const vehicleHandle = record.handle || vehicleLabel(selection).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      const submitButton = this.querySelector('.rr-finder__submit');
      let destination;
      let needsTagVerification = false;

      destination = this.nativeFilterDestination(matchingRecords);

      if (!destination && record.url) {
        destination = new URL(record.url, window.location.origin);
      } else if (!destination) {
        const tagFallbackEnabled = this.dataset.enableTagFallback === 'true';
        if (tagFallbackEnabled && vehicleHandle) {
          destination = this.tagSearchDestination(matchingRecords, selection);
          needsTagVerification = true;
        }
      }

      const vehicle = this.vehicleFromRecord(record, selection.year, '', matchingRecords);
      vehicle.handle = vehicleHandle;
      vehicle.fitmentKey = record.key || vehicleHandle;
      vehicle.url = destination ? destination.toString() : '';
      vehicle.label = vehicleLabel(vehicle);

      if (!destination) {
        saveStoredVehicle(this.storageKey, vehicle);
        syncVehicleLabels(vehicle);
        document.dispatchEvent(new CustomEvent('rr:vehicle-change', { detail: vehicle }));
        this.dataset.state = 'error';
        if (this.status) this.status.textContent = this.message('unmappedMessage', '[vehicle] is saved, but its compatible-products page is not mapped yet. Opening a fitment enquiry.', { vehicle: vehicleLabel(vehicle) });
        if (this.dataset.designMode !== 'true') {
          document.dispatchEvent(new CustomEvent('rr:fitment-unavailable', { detail: { vehicle, opener: submitButton } }));
        }
        return;
      }

      if (needsTagVerification && this.dataset.designMode !== 'true') {
        this.submitting = true;
        this.dataset.state = 'loading';
        submitButton?.setAttribute('aria-busy', 'true');
        if (submitButton) submitButton.disabled = true;
        if (this.status) this.status.textContent = this.message('checkingMessage', 'Checking the Road Rex 4x4 fitment catalogue…');

        try {
          const verified = await this.verifyTaggedCollection(destination);
          if (!verified) {
            saveStoredVehicle(this.storageKey, vehicle);
            syncVehicleLabels(vehicle);
            document.dispatchEvent(new CustomEvent('rr:vehicle-change', { detail: vehicle }));
            this.dataset.state = 'error';
            if (this.status) this.status.textContent = this.message('unmappedMessage', '[vehicle] is saved, but its compatible-products page is not mapped yet. Opening a fitment enquiry.', { vehicle: vehicleLabel(vehicle) });
            document.dispatchEvent(new CustomEvent('rr:fitment-unavailable', { detail: { vehicle, opener: submitButton } }));
            return;
          }

          vehicle.url = destination.toString();
        } catch (_error) {
          saveStoredVehicle(this.storageKey, vehicle);
          syncVehicleLabels(vehicle);
          document.dispatchEvent(new CustomEvent('rr:vehicle-change', { detail: vehicle }));
          this.dataset.state = 'error';
          if (this.status) this.status.textContent = this.message('verificationErrorMessage', '[vehicle] is saved. We could not verify the catalogue link, so Road Rex 4x4 will confirm fitment manually.', { vehicle: vehicleLabel(vehicle) });
          document.dispatchEvent(new CustomEvent('rr:fitment-unavailable', { detail: { vehicle, opener: submitButton } }));
          return;
        } finally {
          this.submitting = false;
          submitButton?.removeAttribute('aria-busy');
          if (submitButton) submitButton.disabled = false;
        }
      } else if (needsTagVerification) {
        if (this.status) this.status.textContent = this.message('designModeMessage', '[vehicle] saved. The catalogue link will be verified outside the Theme Editor.', { vehicle: vehicleLabel(vehicle) });
      } else vehicle.url = destination.toString();

      saveStoredVehicle(this.storageKey, vehicle);
      syncVehicleLabels(vehicle);
      document.dispatchEvent(new CustomEvent('rr:vehicle-change', { detail: vehicle }));
      this.dataset.state = 'saved';
      if (this.status) this.status.textContent = this.message('savedMessage', '[vehicle] saved to your garage.', { vehicle: vehicleLabel(vehicle) });

      if (this.dataset.designMode === 'true') return;

      if (vehicle.url) window.setTimeout(() => window.location.assign(vehicle.url), 220);
    }
  }

  if (!customElements.get('road-rex-vehicle-finder')) {
    customElements.define('road-rex-vehicle-finder', RoadRexVehicleFinder);
  }

  class RoadRexProductTabs extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.tabs = [...this.querySelectorAll('[data-rr-product-tab]')];
      this.panels = [...this.querySelectorAll('[data-rr-product-panel]')];

      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.activate(index));
        tab.addEventListener('keydown', (event) => this.handleKeydown(event, index));
      });
    }

    activate(index, moveFocus = false) {
      this.tabs.forEach((tab, tabIndex) => {
        const active = tabIndex === index;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        if (active && moveFocus) tab.focus();
      });
      this.panels.forEach((panel, panelIndex) => {
        panel.hidden = panelIndex !== index;
      });
    }

    handleKeydown(event, index) {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      let nextIndex = index;
      if (event.key === 'ArrowRight') nextIndex = (index + 1) % this.tabs.length;
      if (event.key === 'ArrowLeft') nextIndex = (index - 1 + this.tabs.length) % this.tabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = this.tabs.length - 1;
      this.activate(nextIndex, true);
    }
  }

  if (!customElements.get('road-rex-product-tabs')) {
    customElements.define('road-rex-product-tabs', RoadRexProductTabs);
  }

  const resolveGarageStorageKey = () =>
    document.querySelector('road-rex-vehicle-finder[data-storage-key], road-rex-assistance[data-storage-key]')?.dataset.storageKey || STORAGE_KEY;

  const parseJson = (value, fallback = null) => {
    try {
      return JSON.parse(value);
    } catch (_error) {
      return fallback;
    }
  };

  const compatibilityTokens = (payload) => {
    const tokens = [];
    const visit = (value) => {
      if (Array.isArray(value)) {
        value.forEach(visit);
        return;
      }
      if (value && typeof value === 'object') {
        ['id', 'gid', 'fitmentId', 'fitment_id', 'handle', 'key'].forEach((field) => {
          if (value[field]) tokens.push(cleanValue(value[field]));
        });
        ['fitmentIds', 'fitment_ids', 'fitmentKeys', 'fitment_keys', 'handles'].forEach((field) => {
          if (value[field]) visit(value[field]);
        });
        return;
      }
      if (typeof value === 'string') {
        value
          .split(',')
          .map(cleanValue)
          .filter(Boolean)
          .forEach((token) => tokens.push(token));
      }
    };
    visit(payload);
    return unique(tokens);
  };

  const fitmentLabel = (panel, state, vehicle) => {
    const labels = {
      no_vehicle: panel?.dataset.rrFitmentNoVehicleLabel || 'Choose a vehicle to check the compatibility listing',
      confirmed: panel?.dataset.rrFitmentConfirmedLabel || 'Listed for your saved vehicle',
      incompatible: panel?.dataset.rrFitmentIncompatibleLabel || 'Not listed for your saved vehicle',
      universal: panel?.dataset.rrFitmentUniversalLabel || 'Listed as a universal-fit product',
      manual_check: panel?.dataset.rrFitmentManualLabel || 'Road Rex 4x4 fitment check required',
      unmapped: panel?.dataset.rrFitmentManualLabel || 'Compatibility data is not mapped yet',
      not_applicable: panel?.dataset.rrFitmentNotApplicableLabel || 'Vehicle fitment does not apply'
    };
    const label = labels[state] || labels.manual_check;
    return label.replace(/\[vehicle\]/g, vehicleLabel(vehicle));
  };

  const setCartProperty = (root, selector, value) => {
    const input = root.querySelector(selector);
    if (!(input instanceof HTMLInputElement)) return;
    input.value = cleanValue(value);
    input.disabled = !input.value;
  };

  const setMismatchGate = (root, incompatible) => {
    root.querySelectorAll('[data-rr-add-button]').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      if (incompatible && !button.disabled) {
        button.disabled = true;
        button.dataset.rrDisabledByFitment = 'true';
      } else if (!incompatible && button.dataset.rrDisabledByFitment === 'true') {
        button.disabled = false;
        delete button.dataset.rrDisabledByFitment;
      }
    });
  };

  const updateProductCompatibility = (root, vehicle) => {
    const source = root.querySelector('[data-rr-product-fitment]');
    const payload = source ? parseJson(source.textContent || '{}', {}) : {};
    const mode = cleanValue(payload.mode || root.dataset.rrFitmentMode || 'unmapped').toLowerCase().replace(/[-\s]+/g, '_');
    const listedTokens = compatibilityTokens(payload.vehicles || payload.compatibleVehicles || payload.compatible_ids || []);
    // A saved year can legitimately overlap a broad generation record and one
    // or more exact product-application ranges. Keep all of those identifiers
    // available so product-page compatibility is inclusive for the selected
    // year rather than depending on whichever matching record sorted first.
    const vehicleTokens = vehicle ? compatibilityTokens(vehicle) : [];
    const isListed = vehicleTokens.some((vehicleToken) =>
      listedTokens.some((listedToken) => valuesEqual(vehicleToken, listedToken))
    );

    let state = 'unmapped';
    if (mode === 'universal') state = 'universal';
    else if (mode === 'not_applicable') state = 'not_applicable';
    else if (mode === 'manual' || mode === 'manual_check') state = 'manual_check';
    else if (mode === 'specific' && !vehicle) state = 'no_vehicle';
    else if (mode === 'specific' && vehicle && !vehicleTokens.length) state = 'manual_check';
    else if (mode === 'specific' && listedTokens.length && isListed) state = 'confirmed';
    else if (mode === 'specific' && listedTokens.length) state = 'incompatible';

    root.dataset.rrFitmentState = state;
    const panel = root.querySelector('[data-rr-fitment-panel]');
    if (panel) {
      panel.dataset.rrFitmentState = state;
      const message = panel.querySelector('[data-rr-fitment-message]');
      if (message) message.textContent = fitmentLabel(panel, state, vehicle);

      const currentVehicle = panel.querySelector('[data-rr-current-vehicle]');
      if (currentVehicle) {
        if (vehicle && !['universal', 'not_applicable'].includes(state)) currentVehicle.textContent = vehicleLabel(vehicle);
        else if (['universal', 'not_applicable'].includes(state)) currentVehicle.textContent = fitmentLabel(panel, state, vehicle);
        else currentVehicle.textContent = currentVehicle.dataset.rrEmptyLabel || 'Choose your 4\u00d74';
      }
    }

    setCartProperty(root, '[data-rr-cart-vehicle-property]', vehicleLabel(vehicle));
    setCartProperty(root, '[data-rr-cart-vehicle-reference-property]', vehicle?.fitmentId || vehicle?.fitmentKey || vehicle?.handle || '');
    setCartProperty(root, '[data-rr-cart-fitment-state-property]', state === 'confirmed' ? 'Listed compatibility' : state.replace(/_/g, ' '));
    setMismatchGate(root, state === 'incompatible');

    root.dispatchEvent(
      new CustomEvent('rr:product-compatibility', {
        bubbles: true,
        detail: { state, mode, vehicle, listedVehicles: payload.vehicles || [] }
      })
    );
  };

  const refreshGarageUI = (vehicle = readStoredVehicle(resolveGarageStorageKey())) => {
    syncVehicleLabels(vehicle);
    document.querySelectorAll('road-rex-product, [data-rr-product-compatibility]').forEach((root) => {
      updateProductCompatibility(root, vehicle);
    });
  };

  const initializeGarageUI = () => refreshGarageUI();
  const initializeFitmentSearchPage = () => {
    const vehicleLabelParam = boundedValue(new URLSearchParams(window.location.search).get('rr_vehicle_label'), 180);
    if (!vehicleLabelParam) return;

    document.querySelectorAll('[data-rr-search-term]').forEach((node) => {
      node.textContent = `“${vehicleLabelParam}”`;
    });
    document.querySelectorAll('[data-rr-search-input], .rr-header-search__input').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      input.value = '';
      input.placeholder = `Search parts for ${vehicleLabelParam}`;
    });
  };

  const initializeRoadRexCore = () => {
    initializeGarageUI();
    initializeFitmentSearchPage();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeRoadRexCore, { once: true });
  else initializeRoadRexCore();

  document.addEventListener('rr:vehicle-change', (event) => refreshGarageUI(event.detail || null));
  window.addEventListener('storage', (event) => {
    if (event.key === resolveGarageStorageKey()) refreshGarageUI(readStoredVehicle(resolveGarageStorageKey()));
  });

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      const action = form.getAttribute('action') || '';
      if (!action.includes('/cart/add')) return;

      const productRoot = form.closest('road-rex-product, [data-rr-product-compatibility]');
      if (productRoot?.dataset.rrFitmentState === 'incompatible') {
        event.preventDefault();
        event.stopImmediatePropagation();
        productRoot.querySelector('[data-rr-fitment-panel]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      const vehicle = readStoredVehicle(resolveGarageStorageKey());
      let vehicleInput = form.querySelector('input[data-rr-vehicle-property="true"]');

      if (!vehicle || !FIELDS.every((field) => vehicle[field])) {
        if (vehicleInput) vehicleInput.disabled = true;
        return;
      }

      vehicleInput ||= form.querySelector('input[name="properties[Vehicle]"]');
      if (!vehicleInput) {
        vehicleInput = document.createElement('input');
        vehicleInput.type = 'hidden';
        vehicleInput.name = 'properties[Vehicle]';
        form.append(vehicleInput);
      }
      vehicleInput.dataset.rrVehicleProperty = 'true';
      vehicleInput.value = vehicleLabel(vehicle);
      vehicleInput.disabled = false;
    },
    true
  );
})();
